<template>
  <div>
    <ui-dialog
      :show="isOpen"
      persistent
      scrollable
      :max-width="isLabelGroup ? '623' : '500'"
      @input="close"
    >
      <template #header>
        <h3>
          {{ dialogTitle }}
        </h3>
        <SelectedCompany class="mt-3" />
      </template>
      <template #default>
        <v-form
          v-model="valid"
          ref="form"
          lazy-validation
          @submit.prevent="submit"
        >
          <v-text-field
            v-model="newGroupName"
            ref="input"
            v-validate="'required'"
            :data-vv-name="
              $t(
                `message.businessAddresses.groups.${
                  group ? 'editGroup' : 'addNewGroup'
                }.newGroupName`
              )
            "
            :error-messages="
              errors.collect(
                $t(
                  `message.businessAddresses.groups.${
                    group ? 'editGroup' : 'addNewGroup'
                  }.newGroupName`
                )
              )
            "
            :label="
              $t(
                `message.businessAddresses.groups.${
                  group ? 'editGroup' : 'addNewGroup'
                }.newGroupName`
              )
            "
            required
          ></v-text-field>
        </v-form>
        <div v-if="isLabelGroup">
          <div class="section-title">
            {{ $t("message.facilityDetails.labels.title") }}
            <v-btn
              v-if="newLabels && newLabels.length"
              icon
              color="primary"
              @click="createLabelsGroupDialogVisible = true"
            >
              <v-icon>
                icon-edit
              </v-icon>
            </v-btn>
          </div>
          <template v-if="newLabels && newLabels.length === 0">
            <ui-button
              block
              outlined
              color="primary"
              icon="mdi-plus"
              class="mt-2"
              @click="createLabelsGroupDialogVisible = true"
            >
              {{ $t("message.common.add") }}
            </ui-button>
          </template>
          <template v-else>
            <v-chip
              v-for="(label, index) in newLabels"
              :key="`${label}_${index}`"
              outlined
              class="mr-2 my-1"
            >
              {{ label }}
              <v-icon
                class="ml-2 mr--1"
                color="grey"
                @click="deleteLabel(label)"
              >
                icon-close
              </v-icon>
            </v-chip>
          </template>
        </div>
      </template>
      <template #actions>
        <ui-button outlined @click="close">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button color="primary" @click="submit" :disabled="applyBtnDisabled">
          {{ $t("message.common.save") }}
        </ui-button>
      </template>
    </ui-dialog>

    <GmbLabelsCreateGroupDialog
      :is-open="createLabelsGroupDialogVisible"
      :initiallySelectedLabels="newLabels || []"
      @close="createLabelsGroupDialogVisible = false"
      @save="saveLabels"
    />
  </div>
</template>

<script>
import SelectedCompany from "@/components/Permissions/SelectedCompany";
import GmbLabelsCreateGroupDialog from "@/components/FacilityDetails/GmbLabels/GmbLabelsCreateGroupDialog.vue";

export default {
  name: "AddNewGroupDialog",
  components: {
    SelectedCompany,
    GmbLabelsCreateGroupDialog
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    group: {
      type: Object,
      required: false,
      default: null
    },
    isAddingLabelGroup: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: false,
      newGroupName: "",
      newLabels: [],
      createLabelsGroupDialogVisible: false
    };
  },
  computed: {
    isLabelGroup() {
      return (
        (!!this.group && this.group.is_label_group) || this.isAddingLabelGroup
      );
    },
    dialogTitle() {
      return this.$t(
        `message.businessAddresses.groups.${
          this.group ? "editGroup" : "addNewGroup"
        }.title`
      );
    },
    applyBtnDisabled() {
      return this.isLabelGroup
        ? !this.newGroupName || !this.newLabels.length
        : !this.newGroupName;
    }
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        if (this.group && this.group.is_label_group) {
          this.newLabels = [...this.group.labels];
        }

        if (this.group) {
          this.newGroupName = this.group.name;
        }

        setTimeout(() => {
          this.$refs.input.focus();
        }, 150);
      } else {
        this.newGroupName = "";
        this.newLabels = [];
        this.errors.clear();
      }
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },

    async submit() {
      await this.$validator.validateAll().then(isTrue => {
        isTrue && this.valid ? this.save() : null;
      });
    },

    save() {
      const payload = { name: this.newGroupName };

      if (this.isLabelGroup) {
        payload.is_label_group = true;
        payload.labels = [...this.newLabels];
      }

      if (this.group) {
        payload.id = this.group.id;
        this.$emit("edit", payload);
      } else {
        this.$emit("save", payload);
      }
      this.close();

      this.newGroupName = "";
      this.errors.clear();
    },

    deleteLabel(label) {
      this.newLabels = this.newLabels.filter(l => l !== label);
    },

    saveLabels(labels) {
      this.newLabels = [...labels];
    }
  }
};
</script>

<style lang="scss" scoped>
.section-title {
  @include sectionTitle();
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: $spacing * 2;
  margin-left: $spacing * 2;
}

.v-chip {
  @include vChip();
  white-space: pre-wrap;
}

.mr--1 {
  margin-right: $spacing * -0.75;
}
</style>
